import { useState } from 'react';
import Header from '../header/header';
import Footer from '../footer/footer';
import PatientStories from '../PatientStories/PatientStories';
import FAQ from '../FAQ/FAQ';
import AsSeenOn from '../AsSeenOn/AsSeenOn';
import HowItWorks from '../HowItWorks/HowItWorks';
import CyclingText from '../../Landing/CyclingText';
import Pricing from '../../../data/Pricing';
import styles from './CompoundedLanding.module.css';

const DEFAULT_SAFETY_MESSAGES = [
    'Compounded medication — not FDA-approved',
    'A licensed clinician evaluates every intake',
    'Prescription included if clinically appropriate',
    'Dispensed by a state-licensed pharmacy',
    'Not evaluated as equivalent to brand-name versions',
    'Individual results may vary',
];

/**
 * Landing page template for a single compounded product (/semaglutide, /nad).
 * Every page-specific string and image comes in through props; the sections
 * below `safety` that only some products have (research, smallprint) render
 * only when passed.
 */
export default function CompoundedLanding({
    safetyMessages = DEFAULT_SAFETY_MESSAGES,
    hero,
    onCtaClick,
    price,
    tabs,
    lockup,
    howItWorks = {},
    showPatientStories = false,
    research,
    safety,
    faq = {},
    smallprint,
}) {
    const [activeTab, setActiveTab] = useState(tabs[0].id);

    const pricing = Pricing();
    const productPricing = pricing.getPricingOptionsByProductName(price.product) ?? pricing.getRetailPrices()[price.product];
    const monthlyPrice = productPricing?.[price.formulation]?.monthly?.total?.split('.')[0] ?? null;
    const quarterlyPrice = productPricing?.[price.formulation]?.threeMonths?.total?.split('.')[0] ?? null;

    return (
        <div className={styles.page}>
            <div className={styles.safetyBar}>
                <span className={styles.safetyDot} aria-hidden="true">•</span>
                <CyclingText phrases={safetyMessages} className={styles.safetyText} interval={3500} />
            </div>

            <Header hideNavigation />

            <section className={styles.hero}>
                <div className={`${styles.heroImage} ${hero.fillImage ? styles.heroImageFill : ''}`}>
                    <img src={hero.image} alt={hero.imageAlt} />
                </div>
                <div className={styles.heroText}>
                    <div className={styles.heroEyebrow}>
                        <span className={styles.heroEyebrowA}>Compounded</span>
                        <span className={styles.heroEyebrowB}>{hero.eyebrow}</span>
                    </div>
                    <h1 className={styles.heroTitle}>
                        <span className={styles.grad}>{hero.title}</span>
                    </h1>
                    <p className={styles.heroSub}>{hero.sub}</p>
                    <p className={styles.heroSubSmall}>{hero.subSmall}</p>
                    <button type="button" className={styles.heroCta} onClick={() => onCtaClick('hero_get_started')}>{hero.ctaLabel}</button>

                    {monthlyPrice && (
                        <div className={styles.priceLine}>
                            ${monthlyPrice}
                            <span className={styles.priceMonth}>/month{quarterlyPrice ? ` or $${quarterlyPrice} for 3 months` : ''}</span>
                            <span className={styles.priceCancel}>Cancel anytime</span>
                        </div>
                    )}
                </div>

                {/* Sibling of .heroText (not nested) so mobile can order the image
                    between the CTA block and the tabs. */}
                <div className={styles.tabs}>
                    <div className={styles.tabRow} role="tablist">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabPanels}>
                        {tabs.map((tab) => (
                            activeTab === tab.id && <div key={tab.id} className={styles.tabPanel}>{tab.body}</div>
                        ))}
                    </div>
                </div>
            </section>

            <AsSeenOn />

            <section className={styles.lockup}>
                <div className={styles.lockupInner}>
                    <div className={`${styles.lockupVial} ${lockup.smallImage ? styles.lockupVialSmall : ''}`}>
                        {lockup.image && <img src={lockup.image} alt={lockup.imageAlt} />}
                    </div>
                    <div className={styles.lockupText}>
                        <div className={styles.lockupEyebrow}>{lockup.eyebrow}</div>
                        <h2 className={styles.lockupName}>Compounded <span className={styles.lockupAccent}>{lockup.accent}</span></h2>
                        <p className={styles.lockupCopy}>
                            {lockup.sentences.map((sentence) => (
                                <span key={sentence.text} className={styles.lockupSentence}>
                                    {sentence.lead && <strong className={styles.lockupLead}>{sentence.lead} </strong>}
                                    {sentence.text}
                                </span>
                            ))}
                        </p>
                    </div>
                </div>
            </section>

            <HowItWorks {...howItWorks} />

            {showPatientStories && <PatientStories />}

            {research && <ResearchTable {...research} />}

            <section className={styles.safety}>
                {safety.title && (
                    <div className={styles.safetyHead}>
                        <div className={styles.safetyEyebrow}>{safety.eyebrow}</div>
                        <h2>{safety.title}</h2>
                    </div>
                )}
                <div className={styles.safetyGrid}>
                    {safety.columns.map((col) => (
                        <div key={col.title} className={styles.safetyCol}>
                            <h3>{col.title}</h3>
                            <div className={styles.safetySub}>{col.sub}</div>
                            {col.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                            {col.strong && <p className={styles.safetyStrong}>{col.strong}</p>}
                            {col.list && (
                                <ul className={styles.safetyList}>
                                    {col.list.map((item) => (
                                        <li key={item}><span className={styles.safetyListDot}></span>{item}</li>
                                    ))}
                                </ul>
                            )}
                            {col.note && <p className={styles.safetyNote}>{col.note}</p>}
                        </div>
                    ))}
                </div>
            </section>

            <FAQ {...faq} />

            {smallprint && (
                <section className={styles.smallprint}>
                    {smallprint.map((item) => (
                        <div key={item.title}>
                            <h5>{item.title}</h5>
                            <p>{item.body}</p>
                        </div>
                    ))}
                </section>
            )}

            <Footer />
        </div>
    );
}

// Educational research table with a reading-level toggle (e.g. plain language vs technical).
function ResearchTable({ eyebrow, title, levels }) {
    const [activeLevel, setActiveLevel] = useState(levels[0].id);
    const level = levels.find((l) => l.id === activeLevel);

    return (
        <section className={styles.research}>
            <div className={styles.researchHead}>
                <div>
                    <div className={styles.researchEyebrow}>{eyebrow}</div>
                    <h2>{title}</h2>
                </div>
                <div className={styles.researchToggle} role="group" aria-label="Reading level">
                    {levels.map((l) => (
                        <button
                            key={l.id}
                            type="button"
                            aria-pressed={activeLevel === l.id}
                            className={activeLevel === l.id ? styles.researchToggleActive : ''}
                            onClick={() => setActiveLevel(l.id)}
                        >
                            {l.label}
                        </button>
                    ))}
                </div>
            </div>
            <p className={styles.researchNote}>{level.note}</p>
            <div className={styles.researchTable}>
                <div className={`${styles.researchRow} ${styles.researchRowHead}`}>
                    {level.headers.map((header) => <div key={header}>{header}</div>)}
                </div>
                {level.rows.map((row) => (
                    <div key={row.goal} className={styles.researchRow}>
                        <div className={styles.researchGoal}>{row.goal}</div>
                        <div>
                            <span className={styles.researchLabel}>{level.headers[1]}</span>
                            <span className={styles.researchCompound}>{row.compound}</span>
                        </div>
                        <div>
                            <span className={styles.researchLabel}>{level.headers[2]}</span>
                            <span className={styles.researchWhy}>{row.why}</span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
